// Word-level prose edits in separate document panes or unified track changes.
import { esc } from './escape.js';
import { changeStops } from '../engine/change-stops.js';

const line = (n, t, cls = '', at = null) => {
  const { l, r } = at || {};
  const loc = `${l ? ` data-ln-l="${l}"` : ''}${r ? ` data-ln-r="${r}"` : ''}`;
  return `<div class="code-line ${cls}"${loc}><span class="ln">${cls === 'del' ? '−' : cls === 'add' ? '+' : n}</span><span class="code">${t || ' '}</span></div>`;
};

export function renderProse(result, view = 'sxs', meta = {}) {
  if (view === 'raw') {
    const rows = (result.render.rawRows || []).map((r) => line(r.ln, esc(r.text), r.cls, r.cls === 'del' ? { l: r.ln } : { r: r.ln })).join('');
    return {
      html: rows + `<div class="cap" style="margin-left:16px">Raw line view — reflowed prose reads as whole lines deleted + re-added. Track changes (Side-by-side / Unified) shows the actual word-level edits.</div>`,
      ribbons: [],
    };
  }
  // Span INDEX is the identity. It is already stable correspondence in the
  // engine's own output, so the renderer stamps it rather than the engine
  // carrying an id: both panes mark the same span with the same number, which
  // is what lets the navigator align them without assuming equal heights.
  //
  // Each changed PASSAGE is wrapped in a tinted band. Every other format gives a
  // changed row a colour and picks the changed word out within it; prose used to
  // colour only the word, so the same comparison read as rows in one format and
  // as specks in another. The band is the stop's own extent — the navigator and
  // the eye agree on where one change begins and ends.
  const passages = passageRanges(result);
  // Span index → its passage, built once: a per-span search of every passage
  // made rendering spans × passages (REL-089).
  const bandAt = new Array(result.render.spans.length).fill(null);
  for (const p of passages) for (let i = p.start; i <= p.end && i < bandAt.length; i++) bandAt[i] ??= p;
  const body = (side) => {
    let html = '';
    let open = null;
    result.render.spans.forEach((sp, i) => {
      const band = bandAt[i];
      if (band !== open) {
        if (open) html += '</span>';
        if (band) html += `<span class="prose-passage" data-passage="${esc(band.id)}">`;
        open = band;
      }
      if (side && sp.type !== 'ctx' && sp.type !== side) return;
      const t = esc(sp.text);
      if (sp.type === 'old') html += `<span class="hlOld" data-span="${i}">${t}</span>`;
      else if (sp.type === 'new') html += `<span class="hlNew" data-span="${i}">${t}</span>`;
      else html += t;
    });
    if (open) html += '</span>';
    return html;
  };
  if (view === 'sxs') {
    return {
      html: `<div class="prose-sxs">` +
        `<section class="prose-pane" aria-label="Original text"><div class="prose-hdr">${esc(meta.leftLabel || 'Original')}</div><div class="prose-diff">${body('old')}</div></section>` +
        `<section class="prose-pane" aria-label="Changed text"><div class="prose-hdr">${esc(meta.rightLabel || 'Changed')}</div><div class="prose-diff">${body('new')}</div></section>` +
        `</div><div class="cap">Original on the left, changed text on the right — removed words in red, added words in green.</div>`,
      ribbons: [],
    };
  }
  const html =
    `<div class="prose-diff">${body()}</div>` +
    `<div class="cap">Word-level track changes across the whole document — reflowed paragraphs read as edits, not a wholesale rewrite.</div>`;
  return { html, ribbons: [] };
}


// The span range of each navigable passage, from the shared stops model — so the
// band, the Crux finding and the navigator all describe the same place.
function passageRanges(result) {
  try {
    return changeStops(result).stops
      .filter((stop) => stop.location?.kind === 'span')
      .map((stop) => ({ id: stop.id, start: stop.location.spanStart, end: stop.location.spanEnd }));
  } catch {
    return []; // a band is decoration; never let it cost the diff itself
  }
}
